import {HTMLAttributeAnchorTarget} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {css, Theme, useTheme} from "@emotion/react";
import {Container} from "@pg-design/grid";
import {flexJustifyCenter, onDesktop} from "@pg-design/helpers-css";
import {flex, flexDirection, m, mt, mv, p, pt, pv} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text";

import {bugsnagNotify} from "../../../../../../utils/bugsnag";
import {dataLayer} from "../../../../../../utils/data_layer";
import {staticLinks} from "../../../../common/app/constants/footer";
import {Brand} from "../../../../components/nav/Brand";
import {useEffectiveCitySlug} from "../../../analytical_platform/hooks/use_effective_city_slug";
import {FooterMenuElement} from "./FooterMenuElement";

interface IProps {
    className?: string;
}

declare global {
    interface Window {
        Cookiebot: {renew: () => void};
    }
}

export const Footer = ({className}: IProps) => {
    const citySlug = useEffectiveCitySlug();
    const {t, i18n} = useTranslation();

    const handleLinkClick = (linkText: string) => {
        dataLayer({
            event: "footer",
            button: linkText,
            city: citySlug
        });
    };

    const scrollToTop = () => {
        setTimeout(() => {
            window.scrollTo(0, 0);
        }, 0);
    };

    const openCookieModal = () => {
        try {
            window.Cookiebot.renew();
        } catch (err) {
            toast.error("Nie udało się otworzyć okna ustawień cookies, spróbuj później");
            bugsnagNotify(err as Error, {source: "openCookieModal"});
        }
    };

    const theme = useTheme();

    return (
        <Container fluid css={footerBg} as="section" className={className}>
            <footer css={footerContent}>
                <nav css={logoAndLinksWrapper}>
                    <Brand css={logoWrapper} />

                    <ul css={links}>
                        {staticLinks(i18n).map((link) => (
                            <FooterMenuElement
                                external={!!link.external}
                                key={link.title}
                                url={link.href}
                                title={link.title}
                                onClick={() => {
                                    if (!link.external) {
                                        scrollToTop();
                                    }
                                    if (link.onClick) {
                                        link.onClick();
                                    }
                                    handleLinkClick(link.title);
                                }}
                                target={link.target as HTMLAttributeAnchorTarget}
                                rel={link.rel as string}
                            />
                        ))}

                        <FooterMenuElement
                            external={true}
                            key="cookie"
                            title={t("nav.cookies")}
                            onClick={openCookieModal}
                        />
                    </ul>
                </nav>

                <div css={copyrightsWrapper}>
                    <Text variant="info_txt_1" color={theme.colors.gray[500]}>
                        {t("footer.copyrights", {
                            year: new Date().getFullYear(),
                            interpolation: {escapeValue: false}
                        })}
                    </Text>
                </div>
            </footer>
        </Container>
    );
};

const footerBg = (theme: Theme) => css`
    background: ${theme.colors.secondary};
`;

const footerContent = css`
    margin: 0 auto;
    ${p(2, 1.5, 2, 1.5)};
    max-width: 1680px;

    ${onDesktop(css`
        ${pv(4)}
    `)}
`;

const links = css`
    ${flex()};
    ${flexDirection("column")};

    ${onDesktop(css`
        column-gap: 2.8rem;
        ${flexDirection("row")};
    `)}
`;

const logoAndLinksWrapper = css`
    ${flex("start", "space-between")}
    ${flexDirection("column-reverse")};
    ${mv(4)};

    ${onDesktop(css`
        align-items: center;
        ${flexDirection("row")};
        ${m(6, 0, 0, 0)};
    `)}
`;

const copyrightsWrapper = css`
    ${flex("initial", "flex-start")}

    ${onDesktop(css`
        ${pt(4)};
        ${flexJustifyCenter};
    `)}
`;

const logoWrapper = (theme: Theme) => css`
    ${mt(2)};

    @media (max-width: ${theme.breakpoints.md}) {
        & img {
            height: 40px;
            width: auto;
        }

        gap: 1rem;
    }

    ${onDesktop(css`
        ${mt(0)}
    `)}
`;
